import { paginationState } from './table';

/**
 * Paginacja rankingu: `1 2 3 … 17 18 ›`.
 *
 * Liczbę stron i granice ruchu bierzemy z tabeli (`getPageCount`, `getCanNextPage`), a okno
 * wokół bieżącej strony z dwóch stałych niżej — przy 18 stronach na pierwszej stronie daje
 * to `1 2 3 … 17 18`.
 * Strzałkę rysujemy tylko wtedy, gdy jest gdzie iść: wyłączona strzałka na pierwszej stronie
 * zajmowałaby miejsce i niczego nie mówiła.
 */

/** Ile stron zawsze widać na obu końcach listy. */
const BOUNDARY = 2;

/** Ile stron widać po każdej stronie bieżącej. */
const SIBLINGS = 1;

const ELLIPSIS = '…';

/**
 * @param {import('@tanstack/table-core').Table} table
 * @param {HTMLElement} container
 * @param {(pageIndex: number) => void} onSelect
 */
export function renderPagination(table, container, onSelect) {
    const pageCount = table.getPageCount();
    const pageIndex = paginationState(table).pageIndex;

    if (pageCount <= 1) {
        container.replaceChildren();

        return;
    }

    const items = [];

    if (table.getCanPreviousPage()) {
        items.push(arrow('‹', 'Poprzednia strona', () => onSelect(pageIndex - 1)));
    }

    pageWindow(pageIndex, pageCount).forEach((entry) => {
        items.push(entry === ELLIPSIS ? gap() : page(entry, pageIndex, onSelect));
    });

    if (table.getCanNextPage()) {
        items.push(arrow('›', 'Następna strona', () => onSelect(pageIndex + 1)));
    }

    container.replaceChildren(...items);
}

/**
 * Widoczne numery stron (indeksy od zera) przeplecione wielokropkami.
 *
 * Dziurę o szerokości jednej strony wypełniamy tą stroną, a nie wielokropkiem — `1 … 3 4 5`
 * jest dłuższe niż `1 2 3 4 5` i mniej mówi.
 *
 * @returns {Array<number|'…'>}
 */
function pageWindow(pageIndex, pageCount) {
    const shown = new Set();

    for (let i = 0; i < Math.min(BOUNDARY, pageCount); i++) {
        shown.add(i);
        shown.add(pageCount - 1 - i);
    }

    for (let i = pageIndex - SIBLINGS; i <= pageIndex + SIBLINGS; i++) {
        if (i >= 0 && i < pageCount) {
            shown.add(i);
        }
    }

    const sorted = [...shown].sort((a, b) => a - b);
    const entries = [];

    sorted.forEach((index, position) => {
        const previous = sorted[position - 1];

        if (position > 0 && index - previous === 2) {
            entries.push(previous + 1);
        } else if (position > 0 && index - previous > 2) {
            entries.push(ELLIPSIS);
        }

        entries.push(index);
    });

    return entries;
}

function page(index, currentIndex, onSelect) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ranking__page';
    button.textContent = String(index + 1);

    if (index === currentIndex) {
        button.classList.add('is-active');
        button.setAttribute('aria-current', 'page');
    } else {
        button.addEventListener('click', () => onSelect(index));
    }

    return button;
}

function arrow(glyph, label, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ranking__page ranking__page--arrow';
    button.textContent = glyph;
    button.setAttribute('aria-label', label);
    button.addEventListener('click', onClick);

    return button;
}

function gap() {
    const span = document.createElement('span');
    span.className = 'ranking__page ranking__page--gap';
    span.textContent = ELLIPSIS;
    span.setAttribute('aria-hidden', 'true');

    return span;
}
